import {
    Content,
    ContentWithMedia,
    GiftWithMedia,
    Playlist,
    ReceivedGift,
} from "JS/Models";
import {
    useGlobalGifts,
    useReceivedMemberAudioGifts,
    useReceivedProspectAudioGifts,
} from "JS/React/Hooks/Gifts";
import { useEffect, useMemo, useState } from "react";
import { getDownloadedAudios, shuffle } from "JS/Helpers";
import { useGlobalAudios } from "JS/React/Hooks/Audio";

export const usePlaybackAudios = (
    playlist: Playlist,
    allAudios: Content[],
    isShuffle: boolean,
    mediaIndex: number,
    bundleIndex: number,
    setQueryParams: (
        params: Partial<{
            index: string;
            bundleIndex: string;
            scrollTo: string | number | string[] | qs.ParsedQs | qs.ParsedQs[];
        }>,
    ) => void,
    isOffline: boolean,
) => {
    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();
    const { loading: rmgLoading, isGift } = useReceivedMemberAudioGifts(
        !isOffline || (receivedMemberAudios && receivedMemberAudios.length > 0),
    );
    const { loading: rmpLoading } = useReceivedProspectAudioGifts(
        !isOffline || !!receivedProspectAudios?.length,
    );
    const [downloadedAudios, setDownloadedAudios] = useState<
        (ContentWithMedia | GiftWithMedia)[]
    >([]);
    const { details, getAudioDetails } = useGlobalAudios();

    const sortedMappedAudios = useMemo(() => {
        return [...playlist?.audios]
            ?.sort((a, b) => +a.sortOrder - +b.sortOrder)
            ?.map((a) => {
                let withoutMedia: Content | ReceivedGift = undefined;
                const selfAudio = allAudios?.find((aa) => aa?.nid === a?.nid);
                if (selfAudio) withoutMedia = selfAudio;
                const memberGiftAudio = receivedMemberAudios?.find(
                    (rmg) => rmg?.nid === a?.nid,
                );
                if (memberGiftAudio) withoutMedia = memberGiftAudio;
                const prospectGiftAudio = receivedProspectAudios?.find(
                    (rgp) => rgp?.nid === a?.nid,
                );
                if (prospectGiftAudio) withoutMedia = prospectGiftAudio;
                if (withoutMedia === undefined) return undefined;
                return {
                    ...withoutMedia,
                    ...getAudioDetails(a.nid),
                } as ContentWithMedia | GiftWithMedia;
            })
            ?.filter((a) => a !== undefined && Object.keys(a).length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        playlist,
        receivedMemberAudios?.length,
        receivedProspectAudios?.length,
        allAudios?.length,
        isOffline,
        details,
    ]);

    const rMGNids = useMemo(() => {
        return receivedMemberAudios
            ?.filter((a) =>
                playlist?.audios?.map((pa) => pa?.nid).includes(a?.nid),
            )
            .map((a) => a?.nid);
    }, [playlist, receivedMemberAudios]);

    const rMPNids = useMemo(() => {
        return receivedProspectAudios
            ?.filter((a) =>
                playlist?.audios?.map((pa) => pa?.nid).includes(a?.nid),
            )
            .map((a) => a?.nid);
    }, [playlist, receivedProspectAudios]);

    const allAudioNids = useMemo(() => {
        return allAudios
            ?.filter((a) =>
                playlist?.audios?.map((pa) => pa?.nid).includes(a?.nid),
            )
            .map((a) => a?.nid);
    }, [playlist, allAudios]);

    const playlistAudios: (GiftWithMedia | ContentWithMedia)[] = useMemo(() => {
        if (!!sortedMappedAudios?.length) {
            let playlist: (GiftWithMedia | ContentWithMedia)[] = [];
            if (isShuffle) {
                const current = sortedMappedAudios[mediaIndex];
                const shuffled = shuffle(
                    sortedMappedAudios.filter((a) => a.nid !== current?.nid),
                );
                playlist = [current, ...shuffled];
                setQueryParams({
                    index: "0",
                    bundleIndex: bundleIndex.toString(),
                });
            } else playlist = sortedMappedAudios;
            if (isOffline)
                return playlist
                    .map((a) => downloadedAudios.find((r) => r.nid === a.nid))
                    .filter((a) => a !== undefined);
            return playlist;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        sortedMappedAudios,
        allAudioNids.length,
        rMGNids.length,
        rMPNids.length,
        isShuffle,
        downloadedAudios,
        isOffline,
    ]);

    const getAudioWithMedia = (index: number) =>
        playlistAudios?.length && playlistAudios[index];

    const currentAudio = useMemo(
        () => getAudioWithMedia(mediaIndex),
        [playlistAudios, mediaIndex],
    );

    useEffect(() => {
        const c = async () => {
            const downloaded = await getDownloadedAudios(sortedMappedAudios);
            setDownloadedAudios(downloaded);
        };
        c();
    }, [sortedMappedAudios]);

    const title = useMemo(() => {
        if (!currentAudio) return "";
        return currentAudio?.media?.length > 1
            ? currentAudio.media[bundleIndex]?.media_title
            : currentAudio.title;
    }, [currentAudio, bundleIndex]);

    return {
        playlistAudios,
        rmgLoading,
        rmpLoading,
        isGift,
        currentAudio,
        getAudioByIndex: getAudioWithMedia,
        sortedMappedAudios,
        title,
    };
};

export const MAX_PLAYLIST_CONTENTS = 400;

export const filterSelectedContents = (
    contents: (Content | ReceivedGift)[],
    selectedNids: string[],
    limit = MAX_PLAYLIST_CONTENTS,
) => {
    const selected = contents.filter((c) => selectedNids.includes(c.nid));
    if (selected.length > limit) {
        console.warn(
            `filterSelectedContents: ${selected.length} selected exceeds cap of ${limit}; trimming.`,
        );
    }
    return selected.slice(0, limit);
};
